import { CircleMinus, Trophy } from 'lucide-react';
import React, { useState } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Label } from '@components/ui/label';
import { Textarea } from '@components/ui/textarea';
import { toast } from '@core/toast';
import { getCdnImage } from '../../../components/utils/cdn-image';
import { UserBadgeV2AdminDto } from './store';

interface RemoveBadgeModalProps {
    open: boolean;
    onClose: () => void;
    badge: UserBadgeV2AdminDto | null;
    onConfirm: (badgeId: number, reason?: string) => Promise<void>;
}

const getLocalizedName = (name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed';
    return name['en'] || name[Object.keys(name)[0]] || 'Unnamed';
};

export const RemoveBadgeModal: React.FC<RemoveBadgeModalProps> = ({
    open,
    onClose,
    badge,
    onConfirm
}) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        if (!badge) return;

        setLoading(true);
        try {
            await onConfirm(badge.id, reason.trim() || undefined);
            toast.success('Badge removed successfully');
            handleClose();
        } catch (error: any) {
            console.error('Failed to remove badge:', error);
            toast.error(
                error?.response?.data?.message || 'Failed to remove badge'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setReason('');
        onClose();
    };

    if (!badge) return null;

    const badgeName = getLocalizedName(badge.badgeLevelName);
    const typeName = getLocalizedName(badge.badgeTypeName);
    const categoryName = getLocalizedName(badge.badgeCategoryName);

    const badgeImageUrl =
        badge.image?.bucket && badge.image?.key
            ? getCdnImage(badge.image.bucket, badge.image.key, {
                  resize: { width: 64, height: 64 }
              })
            : undefined;

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <span className="flex size-10 items-center justify-center rounded-lg bg-destructive/10">
                            <CircleMinus className="size-5 text-destructive" />
                        </span>
                        Remove Badge
                    </DialogTitle>
                    <DialogDescription>
                        This action will revoke the badge from the user
                    </DialogDescription>
                </DialogHeader>

                <Alert variant="warning">
                    <AlertDescription>
                        Are you sure you want to remove this badge? This action
                        can be audited and the user will lose this achievement.
                    </AlertDescription>
                </Alert>

                <div className="flex items-center gap-4 rounded-lg border bg-muted/40 p-4">
                    <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-warning/20">
                        {badgeImageUrl ? (
                            <img
                                src={badgeImageUrl}
                                alt=""
                                className="size-full object-cover"
                            />
                        ) : (
                            <Trophy className="size-7 text-warning" />
                        )}
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">
                                {badgeName}
                            </span>
                            <Badge variant="secondary">
                                Level {badge.badgeLevelIndex}
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {categoryName} &bull; {typeName}
                        </p>
                        <span className="text-xs text-muted-foreground">
                            Earned{' '}
                            {new Date(badge.earnedAt).toLocaleDateString()}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="remove-badge-reason">
                        Reason for removal
                    </Label>
                    <Textarea
                        id="remove-badge-reason"
                        rows={3}
                        placeholder="Enter reason for removing this badge..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                        Optional - This reason will be stored for audit purposes
                    </p>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        loading={loading}
                    >
                        <CircleMinus />
                        Remove Badge
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
